import { useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  LoadingButton,
  Stack,
  Typography,
  toast,
} from "@altimateai/lego";
import { IconCopy, IconPlus } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import dayjs from "dayjs";
import { createReferralCode, type ReferralCode } from "../../services/referrals";
import {
  REFERRAL_CODE_PATTERN,
  REFERRAL_CODE_PATTERN_MESSAGE,
} from "../types";

const formSchema = z.object({
  code: z
    .string()
    .trim()
    .refine(v => v === "" || REFERRAL_CODE_PATTERN.test(v), REFERRAL_CODE_PATTERN_MESSAGE),
  max_uses: z.coerce
    .number({ invalid_type_error: "Enter a number" })
    .int("Must be a whole number")
    .min(1, "Must be at least 1")
    .or(z.literal("")),
  expires_at_local: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateReferralCodeForm = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [lastCreated, setLastCreated] = useState<ReferralCode | null>(null);
  const [hasMaxUses, setHasMaxUses] = useState(false);
  const [hasExpiry, setHasExpiry] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { code: "", max_uses: "", expires_at_local: "" },
  });

  const resetForm = () => {
    form.reset({ code: "", max_uses: "", expires_at_local: "" });
    setHasMaxUses(false);
    setHasExpiry(false);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: createReferralCode,
    onSuccess: created => {
      if (!created) return;
      setLastCreated(created);
      queryClient.invalidateQueries({ queryKey: ["referralCodes"] });
      toast({ description: `Referral code ${created.code} created` });
      resetForm();
      setOpen(false);
    },
    onError: (err: unknown) => {
      const message = typeof err === "string" ? err : "";
      if (message === "referral_code_already_exists") {
        form.setError("code", { message: "That code is already taken — pick another." });
        return;
      }
      toast({
        title: "Failed to create referral code",
        description: message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: FormValues) => {
    const maxUses = hasMaxUses && typeof values.max_uses === "number" ? values.max_uses : null;
    const expiresAt =
      hasExpiry && values.expires_at_local
        ? dayjs(values.expires_at_local).toISOString()
        : null;

    mutate({
      code: values.code || undefined,
      max_uses: maxUses,
      expires_at: expiresAt,
    });
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({ description: `Copied ${code} to clipboard` });
    } catch {
      toast({ description: "Could not copy to clipboard", variant: "destructive" });
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!isPending) {
      setOpen(next);
      if (next) {
        resetForm();
        setLastCreated(null);
      }
    }
  };

  const minExpiry = useMemo(() => dayjs().format("YYYY-MM-DDTHH:mm"), []);

  return (
    <>
      <Button onClick={() => handleOpenChange(true)}>
        <IconPlus className="al-size-4" />
        Create code
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create referral code</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom code</FormLabel>
                    <FormControl>
                      <Input placeholder="auto-generate" {...field} disabled={isPending} />
                    </FormControl>
                    <Typography variant="caption" className="al-text-muted-foreground">
                      {REFERRAL_CODE_PATTERN_MESSAGE}. Leave blank to auto-generate. Stored lowercase.
                    </Typography>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="al-space-y-2">
                <Stack direction="row" align="center" gap="sm">
                  <Checkbox
                    id="has-max-uses"
                    checked={hasMaxUses}
                    onCheckedChange={v => {
                      setHasMaxUses(!!v);
                      if (!v) form.setValue("max_uses", "");
                    }}
                    disabled={isPending}
                  />
                  <Label htmlFor="has-max-uses" className="al-font-normal al-cursor-pointer">
                    Limit number of uses
                  </Label>
                </Stack>
                {hasMaxUses && (
                  <FormField
                    control={form.control}
                    name="max_uses"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            placeholder="e.g. 10"
                            {...field}
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="al-space-y-2">
                <Stack direction="row" align="center" gap="sm">
                  <Checkbox
                    id="has-expiry"
                    checked={hasExpiry}
                    onCheckedChange={v => {
                      setHasExpiry(!!v);
                      if (!v) form.setValue("expires_at_local", "");
                    }}
                    disabled={isPending}
                  />
                  <Label htmlFor="has-expiry" className="al-font-normal al-cursor-pointer">
                    Set expiration date
                  </Label>
                </Stack>
                {hasExpiry && (
                  <FormField
                    control={form.control}
                    name="expires_at_local"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            min={minExpiry}
                            {...field}
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <LoadingButton type="submit" loading={isPending}>
                  Create
                </LoadingButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {lastCreated && (
        <Alert className="al-mt-4">
          <AlertDescription>
            <Stack direction="row" gap="sm" align="center">
              <span>
                Created <code className="al-font-mono al-font-semibold">{lastCreated.code}</code>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyCode(lastCreated.code)}
                aria-label={`Copy ${lastCreated.code}`}
              >
                <IconCopy className="al-size-4" />
                Copy
              </Button>
            </Stack>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
